import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";

export type LeadConversionReadiness =
  InterludeTypes["LeadConversionReadinessResponse"];

/**
 * An account can record identifier-only (Enhanced Conversions for Leads) uploads only
 * when both Google Ads toggles are on. `undefined` (not yet loaded / errored) is treated
 * as ready so the modal stays silent — the check is advisory, never a blocker.
 */
export function isLeadConversionReady(
  readiness: LeadConversionReadiness | undefined,
): boolean {
  if (!readiness) return true;
  return (
    readiness.acceptedCustomerDataTerms &&
    readiness.enhancedConversionsForLeadsEnabled
  );
}

/**
 * Live readiness of an ad account for identifier-only (Enhanced Conversions for
 * Leads) uploads. Disabled while accountId is unset (0) so it doesn't fire a
 * 404-bound request before the user picks an account. Not cached aggressively:
 * enabling the Google Ads toggles is eventually consistent, so the operator
 * expects a fresh read each time the account is (re)selected.
 */
export function useLeadConversionReadiness(orgUuid: string, accountId: number) {
  return interlude.googleAds.useGetLeadConversionReadiness(
    {
      params: {
        orgUuid,
        googleAdsAccountId: accountId,
      },
    },
    {
      enabled: accountId > 0 && orgUuid !== "",
    },
  );
}
